import { Inject, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { UrlFormatter } from 'shared';
import { Environment, ENVIRONMENT_TOKEN } from 'account-hybrid/core/environment.service';

/**
 * Native port of the FULL v1 `livePreview` factory (account-v1/src/core/
 * livePreview/livePreview.js). WS5 #10 Stage A inc 2t shipped the TEST-EMAIL
 * slice (sendTestEmail/showTestEmail — the certificate-email editors); inc 2w
 * adds the WHOLE remaining surface for the ft-touchpoint-messaging drain
 * (ft-touchpoint-integration next). Method NAMES are load-bearing: the messaging
 * component invokes the photo-advocacy senders DYNAMICALLY by string
 * (`livePreview[method]({...})` — finalistEmail/reminderEmail/activityEmail/
 * winnerEmail).
 *
 * QUIRKS PRESERVED:
 * - Every sample-email sender POSTs with NO BODY — the model rides the QUERY
 *   STRING via appendSearch after the path tokens fill (byte-identical request
 *   line; v1 passed only the formatted URL to $http.post).
 * - `poststay` opens the blank window SYNCHRONOUSLY (popup-blocker-safe), GETs
 *   the preview URL, then sets `window.location`. The response is parsed like
 *   $http's default transform (try JSON.parse, fall back to the raw text). No
 *   rejection handler, exactly like v1 — on failure the blank window stays open.
 * - `landing`/`mobileLanding`/`followup` open `model.domain + formatted-path`
 *   (the `:code-i` token form relies on UrlFormatter's verbatim `:code`
 *   regex replacement inside the longer token, as in v1).
 * - `confirmation`/`photoRiver`/`customDemoPage` are /demo pages
 *   on `environment.v1BaseUrl` via `UrlFormatter.combineUrlPath` (the
 *   byte-identical v1 urlFormatter port); `customDemoPage` MUTATES the caller's
 *   model (`model.debug = 1`) exactly like v1.
 * - `sendTestEmail`/`showTestEmail` unchanged from inc 2t.
 */
@Injectable({ providedIn: 'root' })
export class LivePreviewService {
  private readonly http = inject(HttpClient);

  constructor(@Inject(ENVIRONMENT_TOKEN) private readonly environment: Environment) {}

  /** v1 `livePreview.redeemEmail(model)`. */
  redeemEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/homepage/touchpoints/:touchpointUUID/emails/homepage/sendemail', model);
  }

  /** v1 `livePreview.followupEmail(model)`. */
  followupEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/prestay/touchpoints/:touchpointUUID/emails/followup/sendemail', model);
  }

  /** v1 `livePreview.poststayEmail(model)`. */
  poststayEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/poststay/touchpoints/:touchpointUUID/emails/invitation-email/sendemail', model);
  }

  /** v1 `livePreview.poststayNonViewEmail(model)`. */
  poststayNonViewEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/poststay/touchpoints/:touchpointUUID/emails/nonview/sendemail', model);
  }

  /** v1 `livePreview.poststayAfterViewEmail(model)`. */
  poststayAfterViewEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/poststay/touchpoints/:touchpointUUID/emails/afterview/sendemail', model);
  }

  /** v1 `livePreview.poststayNoQuoteEmail(model)`. */
  poststayNoQuoteEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/poststay/touchpoints/:touchpointUUID/emails/no-quote/sendemail', model);
  }

  /** v1 `livePreview.poststayNoPhotoEmail(model)`. */
  poststayNoPhotoEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/poststay/touchpoints/:touchpointUUID/emails/no-photo/sendemail', model);
  }

  /** v1 `livePreview.finalistEmail(model)` (dynamic-name call site). */
  finalistEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/photo-advocacy/touchpoints/:touchpointUUID/emails/finalist/sendemail', model);
  }

  /** v1 `livePreview.reminderEmail(model)` (dynamic-name call site). */
  reminderEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/photo-advocacy/touchpoints/:touchpointUUID/emails/finalistreminder/sendemail', model);
  }

  /** v1 `livePreview.activityEmail(model)` (dynamic-name call site). */
  activityEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/photo-advocacy/touchpoints/:touchpointUUID/emails/finalistactivity/sendemail', model);
  }

  /** v1 `livePreview.winnerEmail(model)` (dynamic-name call site). */
  winnerEmail(model: any): Promise<any> {
    return this.sendEmailPost('/api/:propertyUUID/properties/layers/photo-advocacy/touchpoints/:touchpointUUID/emails/winnernotification/sendemail', model);
  }

  /** v1 `livePreview.sendTestEmail(model)`. */
  sendTestEmail(model: any): Promise<any> {
    return firstValueFrom(
      this.http.post(
        UrlFormatter.format('/api/:propertyUuid/properties/:propertyUuid/communication/send-sample-email', model, true),
        null,
      ),
    );
  }

  /** v1 `livePreview.showTestEmail(model)`. */
  showTestEmail(model: any): void {
    const win = window.open('');
    const url = UrlFormatter.format(
      '/api/:propertyUuid/properties/:propertyUuid/communication/get-sample-email-html',
      model,
      true,
    );
    firstValueFrom(this.http.get(url, { responseType: 'text' })).then(
      (html: string) => {
        win?.document.write(html);
        win?.document.close();
      },
      () => {
        win?.close();
      },
    );
  }

  /** v1 `livePreview.confirmation(model)` — /demo confirmation page on v1BaseUrl. */
  confirmation(model: any): void {
    const url = UrlFormatter.combineUrlPath(
      (this.environment as any).v1BaseUrl,
      UrlFormatter.format('/demo/prestay/confirmation?cid=:fullCode&lang=:code&debug=1', model, true),
    );
    window.open(url);
  }

  /** v1 `livePreview.landing(model)`. */
  landing(model: any): void {
    const url = UrlFormatter.format(':code-i?debug=1', model);
    window.open(model.domain + url);
  }

  /** v1 `livePreview.mobileLanding(model)`. */
  mobileLanding(model: any): void {
    const url = UrlFormatter.format(':code-i?mobile=true', model);
    window.open(model.domain + url);
  }

  /** v1 `livePreview.followup(model)`. */
  followup(model: any): void {
    const url = UrlFormatter.format(':code/share', model);
    window.open(model.domain + url);
  }

  /** v1 `livePreview.poststay(model)` — sync blank window, then GET the preview URL. */
  poststay(model: any): void {
    const win = window.open('');
    firstValueFrom(
      this.http.get(
        UrlFormatter.format('/api/:propertyUUID/properties/:propertyUUID/layers/poststay/preview-url', model, true),
        { responseType: 'text' },
      ),
    ).then((data: string) => {
      // $http's default transform: JSON when it parses, else the raw text.
      let target: any = data;
      try {
        target = JSON.parse(data);
      } catch {
        /* raw text URL */
      }
      (win as any).location = target;
    });
    // v1 had NO rejection handler — the blank window stays open on failure.
  }

  /** v1 `livePreview.photoRiver(model)` — /demo photo-river page on v1BaseUrl. */
  photoRiver(model: any): void {
    const url = UrlFormatter.combineUrlPath(
      (this.environment as any).v1BaseUrl,
      UrlFormatter.format('/demo/photo-river?c=:fullCode&language=:code&debug=1', model),
    );
    window.open(url);
  }

  /** v1 `livePreview.customDemoPage(model)` — mutates the caller's model like v1. */
  customDemoPage(model: any): void {
    model.debug = 1;
    const url = UrlFormatter.combineUrlPath(
      (this.environment as any).v1BaseUrl,
      UrlFormatter.format('/demo/custom/:assetDemoUUID', model, true),
    );
    window.open(url);
  }

  /** The shared sample-email POST shape: no body, the model rides path tokens + query. */
  private sendEmailPost(template: string, model: any): Promise<any> {
    return firstValueFrom(this.http.post(UrlFormatter.format(template, model, true), null));
  }
}
